import { threeWayMergeReport } from "@common/utils/redline/text/three-way-merge";
import { wordDiff } from "@common/utils/redline/text/word-diff";
import { type DiffUnitWithTrailingSeparator, WordTokenizer } from "@common/utils/redline/text/word-tokenizer";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import { editableTargetsOf, textOfTarget, withTargetText } from "./editable-table-model";

export interface TextCarriedOverRead {
    text: string;
    keptUsersWordsOverRead: boolean;
}

export interface DraftCarriedOverRead {
    draft: RedlineTextProposal;
    keptUsersWordsOverRead: boolean;
}

export function textCarriedOverRead(textBoxReadBefore: string, textInBox: string, textOfRead: string): TextCarriedOverRead {
    if (textInBox === textBoxReadBefore) return { text: textOfRead, keptUsersWordsOverRead: false };
    if (textOfRead === textBoxReadBefore) return { text: textInBox, keptUsersWordsOverRead: false };
    const merged = threeWayMergeReport({ textWhenProposed: textBoxReadBefore, textProposed: textInBox, textOnPage: textOfRead, textOnPageIsTheDocumentsOwn: true });
    return { text: merged.textToWrite, keptUsersWordsOverRead: merged.contestedRegionCount > 0 };
}

export function draftCarriedOverRead(cardBoxReadBefore: RedlineTextProposal, draft: RedlineTextProposal, cardOfRead: RedlineTextProposal): DraftCarriedOverRead {
    const draftOverReadDocument: RedlineTextProposal = {
        ...draft,
        paragraphs: draft.paragraphs.map((paragraph, index) => ({ ...paragraph, textWhenProposed: cardOfRead.paragraphs[index].textWhenProposed })),
    };
    return editableTargetsOf(draft).reduce<DraftCarriedOverRead>((carried, target) => {
        const carriedText = textCarriedOverRead(textOfTarget(cardBoxReadBefore, target), textOfTarget(draft, target), textOfTarget(cardOfRead, target));
        return {
            draft: withTargetText(carried.draft, target, carriedText.text),
            keptUsersWordsOverRead: carried.keptUsersWordsOverRead || carriedText.keptUsersWordsOverRead,
        };
    }, { draft: draftOverReadDocument, keptUsersWordsOverRead: false });
}

function comparisonKeysOf(units: DiffUnitWithTrailingSeparator[]): string[] {
    return units.map((unit) => WordTokenizer.comparisonKey(unit.text));
}

export function caretOffsetCarriedOver(textBefore: string, textAfter: string, caretOffsetBefore: number): number {
    const unitsBefore = WordTokenizer.diffUnitsWithTrailingSeparators(textBefore);
    const unitsAfter = WordTokenizer.diffUnitsWithTrailingSeparators(textAfter);
    let caretUnitIndex = -1;
    unitsBefore.forEach((unit, unitIndex) => {
        if (unit.startOffset < caretOffsetBefore) caretUnitIndex = unitIndex;
    });
    if (caretUnitIndex < 0) return Math.min(caretOffsetBefore, unitsAfter[0]?.startOffset ?? textAfter.length);
    const caretHunk = wordDiff(comparisonKeysOf(unitsBefore), comparisonKeysOf(unitsAfter)).find((hunk) => hunk.beforeStart <= caretUnitIndex && caretUnitIndex < hunk.beforeEnd);
    if (caretHunk === undefined) return textAfter.length;
    if (caretHunk.tag !== "equal") {
        return caretHunk.afterEnd > caretHunk.afterStart ? unitsAfter[caretHunk.afterEnd - 1].endOffset : unitsAfter[caretHunk.afterStart]?.startOffset ?? textAfter.length;
    }
    const caretUnitAfter = unitsAfter[caretHunk.afterStart + caretUnitIndex - caretHunk.beforeStart];
    const caretOffsetInUnit = caretOffsetBefore - unitsBefore[caretUnitIndex].startOffset;
    return Math.min(caretUnitAfter.startOffset + caretOffsetInUnit, caretUnitAfter.endOffset + caretUnitAfter.trailingSeparator.length, textAfter.length);
}
